import React, { useState, useEffect } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Box, Typography, FormGroup, FormControlLabel, Checkbox, SelectChangeEvent, Alert, Tooltip } from '@mui/material';
import { HoroscopeType } from '../../types/horoscope';

interface HoroscopeTypeStepProps {
  parentHoroscopeType: HoroscopeType;
  parentSelectedInterests: string[];
  interestsAreas: { name: string; title: string }[];
  updateHoroscopeType: (type: HoroscopeType) => void;
  updateSelectedInterests: (interests: string[]) => void;
  onValidationChange: (isValid: boolean) => void;
}

const HoroscopeTypeStep: React.FC<HoroscopeTypeStepProps> = ({
  parentHoroscopeType,
  parentSelectedInterests,
  interestsAreas,
  updateHoroscopeType,
  updateSelectedInterests,
  onValidationChange
}) => {
  const [horoscopeType, setHoroscopeType] = useState<HoroscopeType>(parentHoroscopeType);
  const [selectedInterests, setSelectedInterests] = useState<string[]>(parentSelectedInterests);
  const [interestError, setInterestError] = useState<boolean>(parentSelectedInterests.length === 0);

  // Set initial horoscope type when component mounts
  useEffect(() => {
    setHoroscopeType(horoscopeType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validate interests whenever they change
  useEffect(() => {
    if( selectedInterests.length === 0 ) {
      const allInterests = interestsAreas.map(area => area.title);
      setSelectedInterests(allInterests);
      updateSelectedInterests(allInterests);  
    }
    setInterestError(selectedInterests.length === 0);
    onValidationChange(selectedInterests.length > 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInterests]);

  const handleHoroscopeChange = (e: SelectChangeEvent) => {
    const value = e.target.value as HoroscopeType;
    updateHoroscopeType(value);
    setHoroscopeType(value);
  };

  const handleInterestChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const interestKey = event.target.name;

    setSelectedInterests(prev => {
      const newInterests = event.target.checked
        ? [...prev, interestKey]
        : prev.filter(key => key !== interestKey);

      // Pass selected interests to parent component if the prop is provided
      updateSelectedInterests(newInterests);

      return newInterests;
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <FormControl fullWidth variant="outlined">
        <InputLabel id="horoscope-type-label">סוג הורוסקופ</InputLabel>
        <Select
          labelId="horoscope-type-label"
          label="סוג הורוסקופ"
          value={horoscopeType}
          onChange={handleHoroscopeChange}
          required
        >
          <MenuItem value="daily">יומי</MenuItem>
          <MenuItem value="weekly">שבועי</MenuItem>
          <MenuItem value="monthly">חודשי</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'right' }}>
          תחומי עניין
        </Typography>

        {interestError && (
          <Alert severity="error" sx={{ mb: 2, textAlign: 'right', direction: 'rtl' }}>
            יש לבחור לפחות תחום עניין אחד
          </Alert>
        )}

        <FormGroup>
          {interestsAreas.map((area) => (
            <FormControlLabel
              key={area.title}
              control={
                <Checkbox
                  checked={selectedInterests.includes(area.title)}
                  onChange={handleInterestChange}
                  name={area.title}
                />
              }
              label={
                <Tooltip title={area.name} arrow >
                  <span>{area.title}</span>
                </Tooltip>
              }
              sx={{
                textAlign: 'right',
                direction: 'rtl',
              }}
            />
          ))}
        </FormGroup>
      </Box>
    </Box>
  );
};

export default HoroscopeTypeStep; 